import type { ShootingResult } from '@/objects/generated';
import type IMatrixConfig from './IMatrixConfig';

interface IMatrixStationRoleCellConstructorArgs {
    MatrixConfig: IMatrixConfig;
    StationRoleId: number;
    StationID: number;
    IsStart: boolean;
    IsStartControl: boolean;
    LapNumber: number;
    IsShooting: boolean;
    IsFillerCell: boolean;
    ColumnId: string;
}

export default class MatrixStationRoleCell {
    /**
     * The MatrixConfig for the matrix
     */
    public MatrixConfig: IMatrixConfig;

    /**
     * The stationroleid for this cell
     */
    public StationRoleId: number;

    /**
     * The columnId of the column that this cell is in
     */
    public ColumnId: string;

    /**
     * The stationid for this cell
     */
    public StationID: number;

    /**
     * Wether or not this cell is a start cell
     *
     * This is true if the stationRole it is built from is marked as start
     */
    public IsStart: boolean;

    /**
     * Is Startcontrol set on the stationrole for this cell.
     */
    public IsStartControl: boolean;

    /**
     * The UsedTime (ms) from the active time on this cell
     */
    public RaceContestantTimeUsedTime: number | null;

    /**
     * The ClockTime from the active time on this cell
     */
    public RaceContestantTimeClockTime: Date | null;

    /**
     * The ID of the active time on this cell
     */
    public RaceContestantTimeId: string | null;

    /**
     * The TUID of the active time on this cell
     */
    public RaceContestantTimeTUID: string | null;

    /**
     * The LapNumber for this cell
     *
     * This comes from the StationRole that this cell is built from
     */
    public LapNumber: number;

    /**
     * We need to know if this is the cell that set the HasFinishTime flag on the MatrixRow
     * So we keep that state here
     * This will only be set to true when we get a time on a station that is finish and the lapnumber is the same as the last lap
     */
    public HasFinishTime: boolean;

    /**
     * The speed we have calculated for the cell
     *
     * Only applicable if we have a time
     */
    public SpeedKph: number;

    /**
     * Wether or not we should display a startControlWarning on this cell
     */
    public HasStartControlWarning: boolean;

    /**
     * True if the station is a shooting station for this cell
     */
    public IsShooting: boolean;

    /**
     * This is true if the cell only exists to fill in the grid and will never actually get any data.
     * This is for when we have more than one race and the races have a different number of actual checkpoints.
     */
    public IsFillerCell: boolean;

    /**
     * The number of PenaltyLaps for a shooting . This will only be filled if this is a shooting station
     * */
    public PenaltyLapCount: number;

    /**
     * Number of missed shots reported from backup
     */
    public BackupShootingMisses: number | null;

    /**
     * Possible ShootingResults. Not all Events have the shootingresults available
     *
     * This will take priority over the {@link ShootingMisses} if it exists
     */
    public ShootingResult: ShootingResult | null;

    /**
     * Set to true if the number of misses and the number of penalty laps are not the same (and we dont have a timepenalty on the class)
     */
    public HasPenaltyLapWarning: boolean;

    /**
     * Set to true if the number of backupMisses doesnt match the number of misses we have on the shootingresult (if we have a shootingresult)
     */
    public HasBackupMissesWarning: boolean;

    /**
     * True if this cell has a jump start warning.
     *
     * We want to mark the cells where there is a jump start, meaning the start was before it was supposed to be. (if its after we dont care)
     *
     * This is typically used for rally.
     *
     * For this to work some setup is required
     * Any station that is neutral zone out is considered a start station for this feature.
     * We also need the times to be generated by GPIO, so the stationrole must have "Attach to contestant and round to closest minute" enabled.
     *
     * We are looking for the times generated by GPIO. They will have the original GPIO time set in the freetext of {@link ContestantTime}
     * If that timestamp is BEFORE the actual time of the {@link ContestantTime}, we show the warning
     */
    public HasJumpStartWarning: boolean;

    /**
     * The timestamp parsed from the freetext in the time when we are checking for jump starts.
     * This will have the same date as the {@link ContestantTime}, even if we just have the time in the string.
     *
     * We keep this around so we can show the timestamp in the tooltip of the warning icon
     */
    public JumpStartTime: Date | null;

    /**
     * The clocktime of the time from a backup station
     */
    public BackupClockTime: Date | null = null;

    /**
     * TUID of the backupTime for this cell (I dont know if we need this, but its nice to have)
     */
    public BackupTimeId: string | null = null;

    /**
     * The ID of the ContestantTime (red time) that is currently on this cell
     */
    public ContestantTimeId: string | null = null;

    /**
     * The ClockTime of the ContestantTime (red time) that is currently on this cell
     */
    public ContestantClockTime: Date | null = null;

    /**
     * The relative url of the picture of the shooting target
     */
    public BiathlonPictureRelativeUrl: string | null;

    /**
     * Who (if any) acknowledged the shooting warnings for this cell
     */
    public ShootingWarningsAcknowledgedmentByName: string | null;

    public hasRaceContestantTime(): boolean {
        return this.RaceContestantTimeId !== null;
    }

    public hasBackupTime(): boolean {
        return this.BackupTimeId !== null;
    }

    public hasContestantTime(): boolean {
        return this.ContestantTimeId !== null;
    }

    public resetBackupTime(): void {
        this.BackupClockTime = null;
        this.BackupTimeId = null;
    }

    public resetContestantTime(): void {
        this.ContestantClockTime = null;
        this.ContestantTimeId = null;
    }

    public resetBiathlonPictureRelativeUrl(): void {
        this.BiathlonPictureRelativeUrl = null;
    }

    public resetShootingResult(): void {
        this.ShootingResult = null;
    }

    public resetRaceContestantTime(): void {
        this.RaceContestantTimeUsedTime = null;
        this.RaceContestantTimeClockTime = null;
        this.RaceContestantTimeId = null;
        this.RaceContestantTimeTUID = null;
        this.SpeedKph = 0;
        this.HasStartControlWarning = false;
        this.HasFinishTime = false;
        this.PenaltyLapCount = 0;
        this.HasJumpStartWarning = false;
        this.JumpStartTime = null;
    }

    constructor(args: IMatrixStationRoleCellConstructorArgs) {
        this.MatrixConfig = args.MatrixConfig;
        this.StationRoleId = args.StationRoleId;
        this.ColumnId = args.ColumnId;
        this.StationID = args.StationID;
        this.IsStart = args.IsStart;
        this.IsStartControl = args.IsStartControl;
        this.LapNumber = args.LapNumber;
        this.IsShooting = args.IsShooting;
        this.IsFillerCell = args.IsFillerCell;
        this.RaceContestantTimeUsedTime = null;
        this.RaceContestantTimeClockTime = null;
        this.RaceContestantTimeId = null;
        this.RaceContestantTimeTUID = null;
        this.SpeedKph = 0;
        this.HasStartControlWarning = false;
        this.HasFinishTime = false;
        this.PenaltyLapCount = 0;
        this.BackupShootingMisses = null;
        this.ShootingResult = null;
        this.HasPenaltyLapWarning = false;
        this.HasBackupMissesWarning = false;
        this.HasJumpStartWarning = false;
        this.JumpStartTime = null;
        this.BackupClockTime = null;
        this.BackupTimeId = null;
        this.BiathlonPictureRelativeUrl = null;
        this.ShootingWarningsAcknowledgedmentByName = null;
    }
}
